import React, { useState, useRef, useCallback, useEffect } from 'react';
import lodashThrottle from 'lodash-es/throttle';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

// Grid Unit - 8px
var GU = 8; // These breakpoint values represent minimum screen sizes.

var DEFAULT_BREAKPOINTS = {
  min: 360,
  small: 360,
  medium: 96 * GU,
  large: 144 * GU
};
function withinBreakpointRange(min, max, viewportWidth, breakpoints) {
  var minBreakpoint = breakpoints[min];
  var maxBreakpoint = breakpoints[max]; // Accept "" or -1 indifferently

  if (min === '') {
    min = -1;
  }

  if (max === '') {
    max = -1;
  } // Get the breakpoint unit value if the name is provided


  if (minBreakpoint) {
    min = minBreakpoint;

    if (typeof minBreakpoint !== 'number') {
      throw new Error("Viewport: invalid minimum value (" + min + ").");
    }
  }

  if (maxBreakpoint) {
    max = maxBreakpoint;

    if (typeof maxBreakpoint !== 'number') {
      throw new Error("Viewport: invalid maximum value (" + max + ").");
    }
  } // Throw when breakpoint name/s don't exist


  if (typeof min === 'string' && !minBreakpoint) {
    throw new Error("Viewport: minimum breakpoint \"" + min + "\" doesn't exist.");
  }

  if (typeof max === 'string' && !maxBreakpoint) {
    throw new Error("Viewport: maximum breakpoint \"" + max + "\" doesn't exist.");
  }

  return (min === -1 || viewportWidth >= min) && (max === -1 || viewportWidth < max);
}
function aboveBreakpoint(value, viewportWidth, breakpoints) {
  return withinBreakpointRange(value, -1, viewportWidth, breakpoints);
}
function belowBreakpoint(value, viewportWidth, breakpoints) {
  return withinBreakpointRange(-1, value, viewportWidth, breakpoints);
}

var HEADLESS_VIEWPORT_SIZE = {
  width: 0,
  height: 0
};
var isBrowser = typeof window !== 'undefined';
var ViewportContext = /*#__PURE__*/React.createContext(null);

function getCurrentWindowSize() {
  return isBrowser ? {
    width: window.innerWidth,
    height: window.innerHeight
  } : HEADLESS_VIEWPORT_SIZE;
}

function ViewportProvider(_ref) {
  var _ref$throttle = _ref.throttle,
      throttle = _ref$throttle === void 0 ? 100 : _ref$throttle,
      _ref$breakpoints = _ref.breakpoints,
      breakpoints = _ref$breakpoints === void 0 ? DEFAULT_BREAKPOINTS : _ref$breakpoints,
      children = _ref.children;

  var _useState = useState(_extends({
    breakpoints: breakpoints
  }, getCurrentWindowSize())),
      viewportSize = _useState[0],
      setViewportSize = _useState[1];

  var throttleHandler = useRef(); // Avoid excessive re-renders if non-memoized object literal is provided via breakpoints prop

  var breakpointsAsValue = JSON.stringify(breakpoints);
  var updateWindowSize = useCallback(function () {
    setViewportSize(_extends({
      breakpoints: breakpoints
    }, getCurrentWindowSize())); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpointsAsValue]);
  var resizeStop = useCallback(function () {
    if (!throttleHandler.current) {
      return;
    }

    if (isBrowser) {
      window.removeEventListener('resize', throttleHandler.current);
    }

    throttleHandler.current.cancel();
  }, []);
  var resizeStart = useCallback(function () {
    throttleHandler.current = lodashThrottle(updateWindowSize, throttle);
    updateWindowSize();

    if (isBrowser) {
      window.addEventListener('resize', throttleHandler.current);
    }
  }, [throttle, updateWindowSize]);
  useEffect(function () {
    resizeStart();
    return function () {
      resizeStop();
    };
  }, [resizeStart, resizeStop]);
  return React.createElement(ViewportContext.Provider, {
    value: viewportSize
  }, children);
}
function useViewport() {
  var _React$useContext = React.useContext(ViewportContext),
      width = _React$useContext.width,
      height = _React$useContext.height,
      breakpoints = _React$useContext.breakpoints;

  var within = useCallback(function (min, max) {
    return withinBreakpointRange(min, max, width, breakpoints);
  }, [width, breakpoints]);
  var above = useCallback(function (value) {
    return aboveBreakpoint(value, width, breakpoints);
  }, [width, breakpoints]);
  var below = useCallback(function (value) {
    return belowBreakpoint(value, width, breakpoints);
  }, [width, breakpoints]);
  return {
    width: width,
    height: height,
    breakpoints: breakpoints,
    within: within,
    above: above,
    below: below
  };
}

export { ViewportProvider, useViewport };
//# sourceMappingURL=use-viewport.esm.js.map
